import React from "react";
import { useState, useEffect } from "react";
import { HAMLET_API } from "../Config/config";
import { useNavigate } from "react-router-dom";

//Mui Material Imports
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Container,
  FormGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";

//Custom Components
import DarkWoodCard from "../utils/DarkWoodCard";
import FullJobsRender from "../Pages/FullJobsRender";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Spinner from "../General/Spinner";
import Fetch from "../General/Fetch";

//Custom Hooks
import { Filter } from "../customHooks/Filter";
import { fechtData, getPrivateElements } from "../customHooks/FetchDataHook";
import { Form } from "react-router-dom";
import { orderArrayByKey } from "../utils/generalData/arrayNormalizer";

//Datos Estadisticos
import StatsCollector from "../utils/stats/StatsCollector";
import JobsForNextDays from "../utils/stats/JobsForNextWeeks";
import JobsPerDate from "../utils/stats/JobsPerDate";
import JobsPerClient from "../utils/stats/JobsPerClient";
import JobsPerSeller from "../utils/stats/JobsPerSeller";
import JobsPerType from "../utils/stats/JobsPerType";
import JobsPerPartType from "../utils/stats/JobsPerPartType";
import JobTypes from "./JobTypes";
import NewRadialBar from "../utils/stats/NewRadialBar";
import StockCount from "../utils/stats/stockCount";
import QuotationsPerStatus from "../utils/stats/QuotationsPerStatus";
import QuotationsAmountPerStatus from "../utils/stats/QuotationsAmountPerStatus";
import QuotationsPerClient from "../utils/stats/QuotationsPerClient";
import QuotationsAmountPerClient from "../utils/stats/QuotationsAmountPerClient";
import { use } from "react";

/*  JobFinder component allows users to search for jobs based on various properties.
    It provides a form to select properties, operators, and input queries.
*/

const JobFinder = (props) => {
  // IMPORTANTE: props.entity y props.propertiesMap deben ser provistos por el componente padre
  // props.entity: endpoint para las búsquedas (ejemplo: "jobs")
  // props.propertiesMap: array de propiedades del modelo con sus tipos y etiquetas

  const [useURL, setURL] = useState();
  const [entity, setEntity] = useState(props.entity);
  const [useProperty, setProperty] = useState(props.propertiesMap[0]);
  const [useQuery, setQuery] = useState(null);
  const [useMax, setMax] = useState(null); // Estado para buscar rangos, este es el valor maximo, para el minimo usamos `useQuery`
  const [useQueryType, setQueryType] = useState("string");
  const [useOperator, setOperator] = useState("eq");
  const [useError, setError] = useState(null);
  const [useResponse, setResponse] = useState(null);
  const [useLoading, setLoading] = useState(true);
  const [properties, setProperties] = useState();
  const inputsVariant = props.inputsVariant || "outlined";
  const inputsColor = props.inputsColor || "primary";

  const operators = [
    { value: "eq", label: "Igual" },
    { value: "ne", label: "Diferente" },
    { value: "gt", label: "Mayor que" },
    { value: "gte", label: "Mayor o igual que" },
    { value: "lt", label: "Menor que" },
    { value: "lte", label: "Menor o igual que" },
    { value: "bt", label: "Entre..." },
  ];

  const fetch = async () => {
    try {
      const stock = await getPrivateElements("materiales");
      const users = await getPrivateElements("memberships");
      const jobParts = await getPrivateElements("jobParts");
      const finishings = await getPrivateElements("finishers");
      const companies = await getPrivateElements("empresas");

      setResponse({
        stock,
        users,
        jobParts,
        finishings,
        companies,
      });
      setLoading(false);
    } catch (error) {
      setError(error.response?.data?.message || error.message);
      return error;
    }
    return;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    // Si no hay query, no buscar
    if (!useQuery || useQuery === "") {
      setURL("");
      return;
    }

    // Construir los parámetros de la URL dinámicamente
    const params = new URLSearchParams();
    params.append("Q", useQuery);
    params.append("P", useProperty.field || useProperty.value);
    params.append("OP", useOperator);

    // Si el operador es "bt" (between), agregar el valor máximo
    if (useOperator === "bt" && useMax) {
      params.append("M", useMax);
    }

    setURL(`?${params.toString()}`);
    localStorage.setItem(
      props.entity + "lastJobSearch",
      `?${params.toString()}`
    );
  };

  useEffect(() => {
    try {
      fetch();
    } catch (error) {
      setError(error);
    }
  }, []);

  useEffect(() => {
    setEntity(props.entity);
    setProperties(props.propertiesMap || []);
    setURL(localStorage.getItem(props.entity + "lastJobSearch") || null);
  }, [setURL, setProperties, entity, props.propertiesMap]);

  const urlForm = (
    <Grid container columns={12} spacing={1} width={"96%"} margin={2}>
      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <Card elevation={6}>
          <CardContent>
            <FormControl fullWidth>
              <form name="search" onSubmit={onSubmit}>
                <FormGroup>
                  <Grid container columns={12} spacing={3} width={"100%"}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        id="url_P"
                        select
                        label={useProperty.label || "Propiedad"}
                        variant={inputsVariant}
                        color={inputsColor}
                        placeholder={useProperty.queryLabel}
                        value={useProperty.value}
                        defaultValue={useProperty.value}
                        onChange={(e) => {
                          e.preventDefault();
                          setProperty(
                            properties.find((item) => {
                              if (item.value === e.target.value) {
                                setQueryType(item.queryType);
                                return item;
                              }
                            })
                          );
                          setURL(null);
                        }}
                      >
                        {properties !== undefined &&
                          properties.map((item) => {
                            return (
                              <MenuItem
                                value={item.value}
                                key={item.value}
                                label={item.label}
                              >
                                {item.label}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                    </Grid>
                    {useResponse !== null &&
                      useQueryType === "id" &&
                      useProperty.label === "Tipo de Parte" && (
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                          <TextField
                            select
                            id="queryPartType"
                            label="Tipo de Parte"
                            variant={inputsVariant}
                            color={inputsColor}
                            fullWidth
                            value={useQuery ?? ""}
                            onChange={(e) => {
                              setURL(null);
                              setQuery(e.target.value);
                            }}
                          >
                            {orderArrayByKey(useResponse.jobParts, "Type").map(
                              (part) => (
                                <MenuItem value={part._id} key={part._id}>
                                  {part.Type}
                                </MenuItem>
                              )
                            )}
                          </TextField>
                        </Grid>
                      )}
                    {useResponse !== null &&
                      useQueryType === "id" &&
                      useProperty.label === "Tipo de Trabajo" && (
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                          <TextField
                            select
                            id="queryJobType"
                            label="Tipo de Trabajo"
                            variant={inputsVariant}
                            color={inputsColor}
                            fullWidth
                            value={useQuery ?? ""}
                            onChange={(e) => {
                              setURL(null);
                              setQuery(e.target.value);
                            }}
                          >
                            {JobTypes.map((jobType) => (
                              <MenuItem value={jobType.name} key={jobType.id}>
                                {jobType.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}

                    {useResponse !== null &&
                      useQueryType === "id" &&
                      useProperty.label === "Material de Parte" && (
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                          <Autocomplete
                            id="queryStock"
                            options={useResponse.stock}
                            autoHighlight
                            autoComplete={true}
                            required
                            getOptionLabel={(option) =>
                              `${option.Tipo} ${option.Gramaje} ${option.Marca} (${option.Ancho_Resma} x ${option.Alto_Resma})`
                            }
                            getOptionKey={(option) => `${option._id}`}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setURL(null);
                                setQuery(newValue._id);
                              } else {
                                setQuery("");
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Material"
                                variant={inputsVariant}
                                color={inputsColor}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      )}
                    {useResponse !== null &&
                      useQueryType === "id" &&
                      useProperty.label.startsWith("Acabado") && (
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                          <TextField
                            select
                            id="queryFinisher"
                            label="Terminacion"
                            variant={inputsVariant}
                            color={inputsColor}
                            fullWidth
                            value={useQuery ?? ""}
                            onChange={(e) => {
                              setURL(null);
                              setQuery(e.target.value);
                            }}
                          >
                            {orderArrayByKey(
                              useResponse.finishings,
                              "Proceso"
                            ).map((finisher) => (
                              <MenuItem value={finisher._id} key={finisher._id}>
                                {`${finisher.Proceso} ${finisher.Modelo} ${finisher.Fabricante}`}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    {useResponse !== null && useQueryType === "select" && (
                      <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <TextField
                          select
                          id="querySelect"
                          label={useProperty.label}
                          variant={inputsVariant}
                          color={inputsColor}
                          fullWidth
                          value={useQuery ?? ""}
                          onChange={(e) => {
                            setURL(null);
                            setQuery(e.target.value);
                          }}
                        >
                          {useProperty.options.map((option) => (
                            <MenuItem value={option} key={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {useResponse !== null &&
                      useQueryType === "id" &&
                      useProperty.label === "Cliente" && (
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                          <Autocomplete
                            id="queryCompany"
                            options={useResponse.companies}
                            autoHighlight
                            autoComplete={true}
                            getOptionLabel={(option) => option.Nombre}
                            required
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setURL(null);
                                setQuery(newValue._id);
                              } else {
                                setQuery("");
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Cliente"
                                variant={inputsVariant}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      )}
                    {useResponse !== null &&
                      useQueryType === "id" &&
                      useProperty.label === "Representante" && (
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                          <TextField
                            select
                            id="queryUsers"
                            label="Representante"
                            variant={inputsVariant}
                            color={inputsColor}
                            fullWidth
                            value={useQuery ?? ""}
                            onChange={(e) => {
                              setURL(null);
                              setQuery(e.target.value);
                            }}
                          >
                            {useResponse.users.map((cust) => (
                              <MenuItem
                                value={cust.userId._id}
                                key={cust.userId._id}
                              >
                                {`${cust.userId.Name} ${cust.userId.LastName}`}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    {useResponse !== null && useQueryType === "string" && (
                      <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <TextField
                          id="query"
                          variant={inputsVariant}
                          color={inputsColor}
                          label="Buscar"
                          fullWidth
                          onChange={(e) => {
                            setURL(null);
                            setQuery(e.target.value);
                          }}
                        ></TextField>
                      </Grid>
                    )}
                    {useResponse !== null && useQueryType === "date" && (
                      <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <TextField
                          id="query"
                          type="date"
                          variant={inputsVariant}
                          color={inputsColor}
                          label="Buscar"
                          fullWidth
                          onChange={(e) => {
                            setURL(null);
                            setQuery(e.target.value);
                          }}
                        ></TextField>
                      </Grid>
                    )}
                    {useResponse !== null && useQueryType === "month" && (
                      <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <TextField
                          id="queryMonth"
                          type="month"
                          variant={inputsVariant}
                          color={inputsColor}
                          label="Mes"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            console.log("Selected month:", e.target.value); // Log the selected month
                            setURL(null);
                            setQuery(e.target.value); // ya viene como "YYYY-MM"
                          }}
                        />
                      </Grid>
)}
                    {useResponse !== null && useQueryType === "number" && (
                      <>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            type="Select"
                            id="operator"
                            select
                            label="Operador"
                            variant={inputsVariant}
                            color={inputsColor}
                            fullWidth
                            value={useOperator}
                            onChange={(e) => {
                              setURL(null);
                              setOperator(e.target.value);
                            }}
                          >
                            {operators.map((op) => (
                              <MenuItem value={op.value} key={op.value}>
                                {op.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            type="number"
                            id="query"
                            variant={inputsVariant}
                            color={inputsColor}
                            label={useOperator === "bt" ? "Minimo" : "Buscar"}
                            fullWidth
                            onChange={(e) => {
                              setURL(null);
                              setQuery(e.target.value);
                            }}
                          ></TextField>
                        </Grid>
                        {useOperator === "bt" && (
                          <>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                              <TextField
                                type="number"
                                id="query2"
                                variant={inputsVariant}
                                color={inputsColor}
                                label="Maximo"
                                fullWidth
                                onBlur={(e) => {
                                  setURL(null);
                                  setMax(e.target.value);
                                }}
                              ></TextField>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                    <Grid sx={{ alignSelf: "center" }} size={{ xs: 12, sm: 12, md: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color={inputsColor}
                      >
                        Buscar
                      </Button>
                      <Button
                        variant="outlined"
                        color={inputsColor}
                        onClick={() => {
                          setURL(null);
                          localStorage.removeItem(
                            props.entity + "lastJobSearch"
                          );
                          setQuery(null);
                        }}
                      >
                        Reset
                      </Button>
                    </Grid>
                  </Grid>
                </FormGroup>
              </form>
            </FormControl>
          </CardContent>
        </Card>
      </Grid>
      {useURL !== null && props.entity === "jobs/complete" && (
        <Grid container columns={12} spacing={2} width={"100%"} margin={2}>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <FullJobsRender
              route={props.entity + useURL}
              settings={{ title: "Pedidos", column: "emited", order: "asc" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <StatsCollector route={props.entity + useURL}>
              <JobsForNextDays from={60} to={60} />
              <JobsPerDate />
              <JobsPerClient rank={10} />
              <JobsPerSeller />
              <JobsPerType />
              <JobsPerPartType rank={10} />
            </StatsCollector>
          </Grid>
        </Grid>
      )}
      {useURL !== null && props.entity !== "jobs/complete" && (
        <Grid container columns={12} spacing={2} width={"100%"} margin={2}>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <Fetch collection={"quotations"} subdir={useURL} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <StatsCollector route={props.entity + useURL}>
              <QuotationsPerStatus />
              <QuotationsAmountPerStatus />
              <QuotationsPerClient rank={10} />
              <QuotationsAmountPerClient rank={10} />
              <StockCount />
            </StatsCollector>
          </Grid>
        </Grid>
      )}
    </Grid>
  );

  const inCaseOfError = (
    <ErrorMessage
      message={useError}
      severity={"warning"}
      action={() => {
        setError(null);
      }}
      title={"Error al cargar datos"}
    />
  );

  const inCaseOfLoading = (
    <Container>
      <Spinner />
    </Container>
  );

  return useLoading ? inCaseOfLoading : useError ? inCaseOfError : urlForm;
};

export default JobFinder;
